import * as Fingerprint from "@fingerprint/agent";
import { FINGERPRINT_EVENT_HEADER } from "@repo/common";
import { env } from "@repo/env";

import { isRiskEventRoute } from "./risk-event-routes";

/**
 * Identification may wait this long before the request goes out without it.
 * Fingerprint advises against much less: slow devices fail identification.
 */
const IDENTIFY_TIMEOUT_MS = 3_000;

/**
 * Starts the Fingerprint agent and stamps a fresh identification event id onto
 * every risk-event request, for the server to look the device up with.
 *
 * Off without a public key. Not gated on cookie consent: it identifies only on
 * risk-event requests, for fraud prevention, which the cookie policy lists as
 * strictly necessary alongside Stripe's fraud cookies. Identification failing
 * or timing out sends the request without the header rather than failing it.
 */
export function startFingerprint(): void {
  const apiKey = env.NEXT_PUBLIC_FINGERPRINT_PUBLIC_KEY;
  if (!apiKey) {
    return;
  }
  const agent = Fingerprint.start({
    apiKey,
    region: env.NEXT_PUBLIC_FINGERPRINT_REGION,
    endpoints: env.NEXT_PUBLIC_FINGERPRINT_ENDPOINT,
  });
  const nextFetch = window.fetch;
  window.fetch = async (input, init) => {
    const url = new URL(
      input instanceof Request ? input.url : input,
      location.href,
    );
    const method =
      init?.method ?? (input instanceof Request ? input.method : "GET");
    if (
      url.origin !== location.origin ||
      !isRiskEventRoute(url.pathname, method)
    ) {
      return nextFetch(input, init);
    }
    const headers = new Headers(
      init?.headers ?? (input instanceof Request ? input.headers : undefined),
    );
    try {
      const { event_id } = await agent.get({ timeout: IDENTIFY_TIMEOUT_MS });
      headers.set(FINGERPRINT_EVENT_HEADER, event_id);
    } catch {
      // The request goes out unidentified; the server records the gap.
    }
    return nextFetch(input, { ...init, headers });
  };
}
